import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

/**
 * Modal component for AI Classification Consent
 * Handles user consent for AI classification with warnings about credits and medical advice
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether modal is visible
 * @param {Function} props.onHide - Handler to close modal
 * @param {Object} props.currentImage - Current image object with UUID
 * @param {Function} props.onConsentGiven - Callback when consent is given and confirmed
 * @param {boolean} props.isLoading - Whether consent request is in progress
 */
const AiClassificationConsentModal = ({
  show,
  onHide,
  currentImage,
  onConsentGiven,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const [consentChecked, setConsentChecked] = useState(false);

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (show) {
      setConsentChecked(false);
    }
  }, [show]);

  const handleConfirm = () => {
    if (consentChecked && currentImage?.uuid && onConsentGiven) {
      onConsentGiven(currentImage.uuid);
    }
  };

  const handleCancel = () => {
    setConsentChecked(false);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleCancel} size="lg" centered backdrop="static">
      <Modal.Header closeButton={!isLoading}>
        <Modal.Title className="d-flex align-items-center">
          <span className="me-2">🤖</span>
          {t('skinMoles.consentModal.title')}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="px-4 py-3">
        {/* Credits Warning */}
        <Alert variant="warning" className="mb-3">
          <Alert.Heading className="h6">
            <span className="me-2">💳</span>
            {t('skinMoles.consentModal.creditsWarningTitle')}
          </Alert.Heading>
          <p className="mb-0">
            {t('skinMoles.consentModal.creditsWarningText')}
          </p>
        </Alert>

        {/* Medical Disclaimer */}
        <Alert variant="danger" className="mb-3">
          <Alert.Heading className="h6">
            <span className="me-2">⚕️</span>
            {t('skinMoles.consentModal.medicalDisclaimerTitle')}
          </Alert.Heading>
          <div>
            <p className="mb-2">
              {t('skinMoles.consentModal.medicalDisclaimerText')}
            </p>
            <ul className="mb-0">
              <li>{t('skinMoles.consentModal.disclaimerPoint1')}</li>
              <li>{t('skinMoles.consentModal.disclaimerPoint2')}</li>
              <li>{t('skinMoles.consentModal.disclaimerPoint3')}</li>
            </ul>
          </div>
        </Alert>

        {/* AI Information */}
        <Alert variant="info" className="mb-3">
          <Alert.Heading className="h6">
            <span className="me-2">🔬</span>
            {t('skinMoles.consentModal.aiInfoTitle')}
          </Alert.Heading>
          <p className="mb-0">
            {t('skinMoles.consentModal.aiInfoText')}
          </p>
        </Alert>

        {/* Consent Checkbox */}
        <div className="mt-4 p-3 bg-light rounded">
          <Form.Check
            type="checkbox"
            id="consent-checkbox"
            checked={consentChecked}
            onChange={(e) => setConsentChecked(e.target.checked)}
            disabled={isLoading}
            label={
              <div>
                <strong>{t('skinMoles.consentModal.consentLabel')}</strong>
                <div className="text-muted mt-1">
                  <small>{t('skinMoles.consentModal.consentDetails')}</small>
                </div>
              </div>
            }
            className="consent-checkbox"
          />
        </div>

        {/* Processing indicator */}
        {isLoading && (
          <div className="text-center mt-3">
            <Spinner animation="border" size="sm" className="me-2" />
            <span>{t('skinMoles.consentModal.processing')}</span>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="px-4 py-3">
        <Button
          variant="secondary"
          onClick={handleCancel}
          disabled={isLoading}
        >
          {t('skinMoles.consentModal.cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={!consentChecked || isLoading || !currentImage?.uuid}
          className="d-flex align-items-center gap-2"
        >
          {isLoading && (
            <Spinner animation="border" size="sm" />
          )}
          {t('skinMoles.consentModal.confirm')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AiClassificationConsentModal;
